"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Shield, Target, Building2, Briefcase, Brain, Bot, Database, Plug,
  LayoutDashboard, ChevronDown, Sparkles, Settings, Mail, FileText,
  Users, Lock, Store, ShoppingCart, BarChart3, Layers, MessageSquare,
  Globe, CreditCard, Activity, Network, GitBranch, Smartphone, Tablet,
  Monitor, Radio, ShieldCheck, Upload, ChevronsLeft, ChevronsRight,
  ChevronLeft, ChevronRight, Code2, BookOpen, Key, Webhook, Terminal,
  Package, FileCode, Layout, Palette, Image, Tag, Warehouse, Hash,
  Truck, Contact, UserCog, MapPin, Fingerprint, Bell, LucideIcon,
  Home, Calendar, Inbox, Send, Receipt, Wand2, ScrollText, ShieldAlert,
  CheckSquare, AppWindow, User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { menuApi } from "@/lib/api-client";
import { useTheme } from "@/contexts/theme-context";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Shield, Target, Building2, Briefcase, Brain, Bot, Database, Plug,
  LayoutDashboard, Sparkles, Settings, Mail, FileText, Users, Lock,
  Store, ShoppingCart, BarChart3, Layers, MessageSquare, Globe,
  CreditCard, Activity, Network, GitBranch, Smartphone, Tablet,
  Monitor, Radio, ShieldCheck, Upload, Code2, BookOpen, Key, Webhook,
  Terminal, Package, FileCode, Layout, Palette, Image, Tag, Warehouse,
  Hash, Truck, Contact, UserCog, MapPin, Fingerprint, Bell,
  Home, Calendar, Inbox, Send, Receipt, Wand2, ScrollText, ShieldAlert,
  CheckSquare, AppWindow, User,
};

/** 七维 → 四维（层）映射 */
const bizNavToTier: Record<string, string> = {
  // 工作台（开阳）
  "lingxi": "workbench", "linglong": "workbench", "lingyi": "workbench",
  "linglu": "workbench",
  // 分控台（天璇）
  "lingyao": "tenant-console", "linghe-tenant": "tenant-console",
  "lingyu": "tenant-console", "lingquan": "tenant-console", "lingmen-tenant": "tenant-console",
  // 总控台（天枢）
  "linggen": "admin-console", "linghe-admin": "admin-console",
  "lingzhi": "admin-console", "yaoguang": "admin-console",
  "tianchi": "admin-console", "lingmen-admin": "admin-console",
};

/** 七维标签（侧边栏内分区标题） */
const bizNavLabels: Record<string, { label: string; color: string }> = {
  // 工作台
  "lingxi":      { label: "灵犀·PC",      color: "#22d3ee" },
  "linglong":    { label: "灵珑·H5",      color: "#14b8a6" },
  "lingyi":      { label: "灵翼·APP",     color: "#06b6d4" },
  "linglu":      { label: "灵鹿·小程序",   color: "#0ea5e9" },
  // 分控台
  "lingyao":        { label: "灵耀·自治",      color: "#3b82f6" },
  "linghe-tenant":  { label: "灵核·租户业务",  color: "#8b5cf6" },
  "lingyu":         { label: "灵玉·三方集成",  color: "#a78bfa" },
  "lingquan":       { label: "灵泉·数据中枢",  color: "#0891b2" },
  "lingmen-tenant": { label: "灵门·租户门户",  color: "#c084fc" },
  // 总控台
  "linggen":        { label: "灵根·管租户",   color: "#ef4444" },
  "linghe-admin":   { label: "灵核·管业务",   color: "#f59e0b" },
  "lingzhi":        { label: "灵智·运维监控", color: "#10b981" },
  "yaoguang":       { label: "瑶光·AI",      color: "#b8943f" },
  "tianchi":        { label: "天池·基础设施",  color: "#475569" },
  "lingmen-admin":  { label: "灵门·平台门户", color: "#78716c" },
};

/** 分层定义：工作台 / 分控台 / 控制台 / 中庭 */
const tierDefs = [
  { id: "workbench",       label: "开阳·工作台",   sub: ["lingxi", "linglong", "lingyi", "linglu"],           color: "#22d3ee" },
  { id: "tenant-console",  label: "天权·分控台",   sub: ["lingyao", "linghe-tenant", "lingyu", "lingquan", "lingmen-tenant"],        color: "#3b82f6" },
  { id: "admin-console",   label: "天枢·总控台",   sub: ["linggen", "linghe-admin", "lingzhi", "yaoguang", "tianchi", "lingmen-admin"], color: "#8b5cf6" },
];

interface NavChild {
  title: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children?: NavChild[];
}
interface NavItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  color?: string;
  children?: NavChild[];
  external?: boolean;
}

const fallbackGroups: { label: string; code: string; color: string; bizNav: string; items: NavItem[] }[] = [
  {
    label: "灵根·管租户", code: "/admin", color: "#8b5cf6", bizNav: "linggen",
    items: [
      { title: "平台治理", icon: Shield, href: "/admin/governance" },
      { title: "权限中心", icon: Lock, href: "/admin/auth" },
      { title: "日志审计", icon: Activity, href: "/admin/lingzhi/audit" },
      { title: "系统安全", icon: ShieldCheck, href: "/admin/lingzhi/security" },
    ],
  },
  {
    label: "灵耀·自治", code: "/tenant", color: "#3b82f6", bizNav: "lingyao",
    items: [
      { title: "租户管理", icon: Building2, href: "/tenant/tenant" },
      { title: "角色管理", icon: Shield, href: "/tenant/role" },
      { title: "用户管理", icon: Users, href: "/tenant/user" },
    ],
  },
  {
    label: "灵核·管业务", code: "/admin/linghe-admin", color: "#059669", bizNav: "linghe-admin",
    items: [
      { title: "业务概览", icon: LayoutDashboard, href: "/admin/linghe-admin/overview" },
      { title: "集成管理", icon: Plug, href: "/admin/linghe-admin/integrations" },
      { title: "运营监控", icon: BarChart3, href: "/admin/linghe-admin/monitor" },
    ],
  },
  {
    label: "瑶光·AI赋能", code: "/admin/yaoguang", color: "#b8943f", bizNav: "yaoguang",
    items: [
      { title: "大模型管理", icon: Brain, href: "/admin/yaoguang/models" },
      { title: "智能体", icon: Bot, href: "/admin/yaoguang/agents" },
    ],
  },
];

interface AppSidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  customWidth?: number;
}


// Recursive menu item for nested navigation (supports 3+ levels)
function MenuItem({ item, depth, pathname, isActive, expandedGroups, toggleGroup, fontSize, itemPy, iconSize, sidebarActiveIndicatorClass }: {
  item: NavItem | NavChild;
  depth: number;
  pathname: string;
  isActive: (href: string) => boolean;
  expandedGroups: Record<string, boolean>;
  toggleGroup: (key: string) => void;
  fontSize: string;
  itemPy: string;
  iconSize: string;
  sidebarActiveIndicatorClass: string;
}) {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedGroups[item.title] ?? false;
  const itemHref = item.href;
  const isItemActive = itemHref ? isActive(itemHref) : false;
  const hasActiveChild = hasChildren && item.children!.some(c => c.href && isActive(c.href));
  const Icon = item.icon || ((props: { className?: string }) => <LayoutDashboard {...props} />);

  const paddingLeft = depth === 0 ? "px-3" : depth === 1 ? "pl-6 pr-2" : "pl-9 pr-1";
  const indentWrapper = depth > 0 ? "ml-3 border-l border-border/30" : "";

  return (
    <div className={indentWrapper}>
      {itemHref && !hasChildren ? (
        <Link
          href={itemHref}
          className={cn(
            "relative flex items-center gap-2.5 rounded-lg transition-all duration-200 group/item",
            fontSize, itemPy, paddingLeft,
            isItemActive
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-gray-50 dark:hover:bg-white/5"
          )}
        >
          {isItemActive && depth === 0 && <div className={sidebarActiveIndicatorClass} />}
          <Icon className={cn(depth === 0 ? iconSize : "w-3 h-3", "shrink-0 transition-colors", isItemActive ? "text-stellar-gold" : "group-hover/item:text-foreground")} />
          <span>{item.title}</span>
        </Link>
      ) : (
        <>
          <button
            onClick={() => toggleGroup(item.title)}
            className={cn(
              "relative flex items-center gap-2.5 rounded-lg transition-all duration-200 w-full group/item",
              fontSize, itemPy, paddingLeft,
              hasActiveChild
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-gray-50 dark:hover:bg-white/5"
            )}
          >
            <Icon className={cn(depth === 0 ? iconSize : "w-3 h-3", "shrink-0 transition-colors", hasActiveChild ? "text-stellar-gold" : "group-hover/item:text-foreground")} />
            <span className="flex-1 text-left">{item.title}</span>
            <ChevronDown className={cn("w-3 h-3 transition-transform shrink-0", isExpanded ? "" : "-rotate-90")} />
          </button>
          {isExpanded && hasChildren && (
            <div className="mt-0.5 mb-0.5 space-y-0.5">
              {item.children!.map((child) => (
                <MenuItem
                  key={child.title}
                  item={child}
                  depth={depth + 1}
                  pathname={pathname}
                  isActive={isActive}
                  expandedGroups={expandedGroups}
                  toggleGroup={toggleGroup}
                  fontSize={fontSize}
                  itemPy={itemPy}
                  iconSize={iconSize}
                  sidebarActiveIndicatorClass={sidebarActiveIndicatorClass}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AppSidebar({ collapsed = false, onToggle, customWidth }: AppSidebarProps) {
  const pathname = usePathname();
  const { sidebarDensity, resolvedTheme } = useTheme();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [navGroups, setNavGroups] = useState<typeof fallbackGroups>(fallbackGroups);
  const [menuLoaded, setMenuLoaded] = useState(false);
  const [activeTier, setActiveTier] = useState<string>(() => {
    if (typeof window !== "undefined") return localStorage.getItem("sidebar_tier") || "workbench";
    return "workbench";
  });
  const setTier = (t: string) => { setActiveTier(t); if (typeof window !== "undefined") { localStorage.setItem("sidebar_tier", t); window.dispatchEvent(new CustomEvent("tier-change", { detail: t })); } };

  // Listen for tier changes from top-bar
  useEffect(() => {
    const handler = (e: Event) => {
      const t = (e as CustomEvent).detail;
      if (t) setActiveTier(t);
    };
    window.addEventListener("tier-change", handler);
    return () => window.removeEventListener("tier-change", handler);
  }, []);

  // Density-based sizing
  const isCompact = sidebarDensity === "compact";
  const sidebarW = collapsed ? 68 : (customWidth || (isCompact ? 148 : 178));
  const itemPy = isCompact ? "py-1" : "py-1.5";
  const groupPy = isCompact ? "py-1.5" : "py-2";
  const fontSize = isCompact ? "text-[11px]" : "text-[12px]";
  const groupFontSize = isCompact ? "text-[12px]" : "text-[13px]";
  const iconSize = isCompact ? "w-3.5 h-3.5" : "w-4 h-4";

  // bg style based on theme
  const bgColor = resolvedTheme === "dark" ? "#1e293b" : "#ffffff";

  // Auto-detect tier from current path
  useEffect(() => {
    if (!pathname || pathname === "/") return;
    for (const group of navGroups) {
      const tier = bizNavToTier[group.bizNav];
      if (!tier) continue;
      for (const item of group.items) {
        if (item.href && pathname.startsWith(item.href)) {
          setTier(tier);
          return;
        }
        if (item.children) {
          for (const child of item.children) {
            if (child.href && pathname.startsWith(child.href)) {
              setTier(tier);
              return;
            }
          }
        }
      }
    }
  }, [pathname, navGroups]);

  useEffect(() => {
    menuApi.tree().then((res) => {
      const menus = (res as any).data || res;
      if (!menus || menus.length === 0) return;

      const grouped: Record<string, any[]> = {};
      menus.forEach((m: any) => {
        const biz = m.bizNav || m.biz_nav || "other";
        if (!grouped[biz]) grouped[biz] = [];
        grouped[biz].push(m);
      });

      Object.values(grouped).forEach(items => items.sort((a, b) => (a.sortOrder || a.sort_order || 0) - (b.sortOrder || b.sort_order || 0)));

      const groups: typeof fallbackGroups = [];
      const bizNavOrder = ["lingxi","linglong","lingyi","linglu","lingyao","linghe-tenant","lingyu","lingquan","lingmen-tenant","linggen","linghe-admin","lingzhi","yaoguang","tianchi","lingmen-admin"];

      bizNavOrder.forEach(bizNav => {
        if (!grouped[bizNav]) return;
        const meta = bizNavLabels[bizNav] || { label: bizNav, color: "#64748b" };
        // Yaoguang: only model management + routing (per 7.0 spec)
        const allowedYaoguang = new Set(["menu-a-models", "menu-a-routing"]);
        const mapItem = (m: any): NavItem => ({
          title: m.title || "未命名",
          icon: iconMap[m.icon] || LayoutDashboard,
          href: m.path || undefined,
          children: m.children && m.children.length > 0 ? m.children.map(mapItem) : undefined,
        });
        let items: NavItem[] = grouped[bizNav].map(mapItem);
        if (bizNav === "yaoguang") {
          const rawItems = grouped[bizNav];
          items = rawItems.filter((m: any) => allowedYaoguang.has(m.id)).map(mapItem);
        }
        groups.push({ label: meta.label, code: '/' + bizNav[0].toUpperCase(), color: meta.color, bizNav, items });
      });

      if (groups.length > 0) {
        setNavGroups(groups);
        const defaults: Record<string, boolean> = {};
        groups.forEach(g => {
          defaults[g.label] = true;
          g.items.forEach(item => {
            if (item.children && item.children.length > 0) {
              defaults[item.title] = true;
            }
          });
        });
        setExpandedGroups(defaults);
      }
      setMenuLoaded(true);
    }).catch(() => {
      setMenuLoaded(true);
    });
  }, []);

  // Filter groups by active tier
  const activeTierDef = tierDefs.find(t => t.id === activeTier) || tierDefs[0];
  const visibleGroups = navGroups.filter(g => activeTierDef.sub.includes(g.bizNav));

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => {
      if (prev[label]) return { ...prev, [label]: false };
      return { ...prev, [label]: true };
    });
  };

  const isActive = (href: string) => {
    if (!href) return false;
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  return (
    <aside
      className="relative flex flex-col h-full border-r border-border/50 transition-all duration-300"
      style={{ width: sidebarW + "px", background: bgColor }}
    >
      {collapsed && (
        <div className="flex flex-col items-center gap-1 py-2 border-b border-border/30">
          {tierDefs.map((tier) => (
            <button
              key={tier.id}
              onClick={() => setTier(tier.id)}
              className={cn(
                "w-7 h-7 rounded-lg flex items-center justify-center transition-all",
                activeTier === tier.id
                  ? "shadow-sm"
                  : "opacity-50 hover:opacity-80"
              )}
              style={activeTier === tier.id ? { backgroundColor: tier.color + "20", outlineColor: tier.color, outlineWidth: "2px", outlineStyle: "solid", outlineOffset: "2px" } : {}}
              title={tier.label}
            >
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tier.color }} />
            </button>
          ))}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
        {visibleGroups.map((group) => (
          <div key={group.label} className="mb-1">
            {collapsed ? (
              <div className="flex flex-col items-center gap-1 py-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center border border-border/30 hover:border-stellar-gold/50 hover:bg-stellar-gold/5 transition-all cursor-pointer"
                  style={{ backgroundColor: group.color + "15" }}
                  title={group.label}
                >
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }} />
                </div>
                <span className="text-[9px] text-muted-foreground text-center leading-tight max-w-[60px] truncate">
                  {group.label.split("·")[0].trim()}
                </span>
              </div>
            ) : (
              <>
                <button
                  onClick={() => toggleGroup(group.label)}
                  className={cn("flex items-center gap-2 w-full px-3 font-semibold text-foreground/90 hover:text-foreground transition-colors", groupFontSize, groupPy)}
                >
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                  {group.label}
                  <ChevronDown className={cn("w-3.5 h-3.5 ml-auto transition-transform", expandedGroups[group.label] ? "" : "-rotate-90")} />
                </button>
                {expandedGroups[group.label] && (
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <MenuItem
                        key={item.title}
                        item={item}
                        depth={0}
                        pathname={pathname}
                        isActive={isActive}
                        expandedGroups={expandedGroups}
                        toggleGroup={toggleGroup}
                        fontSize={fontSize}
                        itemPy={itemPy}
                        iconSize={iconSize}
                        sidebarActiveIndicatorClass="sidebar-active-indicator"
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {!menuLoaded && (
          <div className="px-3 py-2 text-[10px] text-muted-foreground animate-pulse">加载菜单...</div>
        )}
        {menuLoaded && visibleGroups.length === 0 && (
          <div className="px-3 py-4 text-[11px] text-muted-foreground text-center">此层暂无菜单</div>
        )}
      </nav>

      <button
        onClick={() => onToggle?.()}
        className="absolute top-1/2 -right-3 w-6 h-6 rounded-full bg-white dark:bg-slate-800 border border-border/60 shadow-sm flex items-center justify-center hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-stellar-gold/40 transition-all z-50"
        title={collapsed ? "展开侧边栏" : "收起侧边栏"}
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>
    </aside>
  );
}


